import { AfterViewInit, Component, ElementRef, OnDestroy, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { connect3AndLevelGamesConnectStatusClass } from 'src/app/gamification/command-center/models/games-player-breakdown';
import { debounce } from 'lodash';
import { DefaultDirection } from 'src/shared/models/metric';
import { GameType } from 'src/app/gamification/command-center/models/games.model';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { TooltipDirective } from '@niceltd/sol/tooltip';
import tippy, { Instance as TippyInstance } from 'tippy.js';

@Component({
    selector: 'cv-connect3-all-result-render',
    templateUrl: './cv-connect3-all-result-render.component.html',
    styleUrls: ['./cv-connect3-all-result-render.component.scss']
})
export class CvConnect3AllResultRenderComponent implements ICellRendererAngularComp, AfterViewInit, OnDestroy {

    data: any;
    statusClass: string;
    type: 'popover' | 'plainValue';
    selectedGame: any;
    isCxOne: boolean;
    fallbackAbbrevText: string;
    getFormattedMetric: (value: any, format: any) => string;
    tippyInstance: TippyInstance | null = null;
    resizeListener: (() => void) | null = null;

    @ViewChild('solPopoverRef') solPopoverInstance: TooltipDirective;
    @ViewChild('connect3MetricsPopover', { static: true }) connect3MetricsPopover!: TemplateRef<any>;
    @ViewChild('popover', { static: false }) popover!: ElementRef;

    constructor(private vcr: ViewContainerRef) { }

    ngAfterViewInit() {
        if (!this.popover ||
            !this.selectedGame?.Connect3Positions ||
            !this.data?.connect3Metrics
        ) return;

        requestAnimationFrame(() => {
            this.initializeTippyPopover();
        });
    }

    ngOnDestroy() {
        if (this.tippyInstance) {
            this.tippyInstance.destroy();
            this.tippyInstance = null;
        }
        if (this.resizeListener) {
            window.removeEventListener('resize', this.resizeListener);
            this.resizeListener = null;
        }
    }


    agInit(params: any): void {
        this.selectedGame = params.selectedGame;
        if (!this.selectedGame) {
            this.type = 'plainValue';
            return;
        };
        this.data = params.data;
        this.isCxOne = params.isCxOne;
        this.fallbackAbbrevText = params.fallbackAbbrevText;
        this.getFormattedMetric = params.getFormattedMetric;
        this.statusClass = connect3AndLevelGamesConnectStatusClass(this.data.resultsStatus, this.isCxOne);
        if (this.selectedGame.Qualifier) {
            if (this.data?.passed) {
                this.type = 'popover';
            } else {
                this.type = 'plainValue';
            }
            return;
        }
        
        if (this.data?.results) {
            this.type = 'popover';
        }
    }

    refresh(): boolean {
        return false;
    }

    initializeTippyPopover() {
        const view = this.vcr.createEmbeddedView(this.connect3MetricsPopover);
        view.detectChanges();

        const container = document.createElement('div');
        container.style.display = 'inline-block';
        view.rootNodes.forEach(node => container.appendChild(node));

        const agBodyViewport = document.querySelector('.ag-body') as HTMLElement;

        this.tippyInstance = tippy(this.popover.nativeElement as HTMLElement, {
            content: container,
            allowHTML: true,
            placement: 'left',
            trigger: 'click',
            interactive: true,
            appendTo: agBodyViewport ?? this.popover.nativeElement.parentElement,
            theme: 'player-breakdown-light',
            popperOptions: {
                modifiers: [
                    {
                        name: 'flip',
                        options: {
                            fallbackPlacements: ['bottom', 'top'], // fallback position
                            padding: 8, // space from the edge of the screen
                        },
                    },
                    {
                        name: 'preventOverflow',
                        options: { boundary: agBodyViewport }
                    },
                ]
            }
        });
        this.resizeListener = debounce(() => {
            if (!this.tippyInstance) return;

            if (this.tippyInstance.state.isVisible) {
                this.tippyInstance.setProps({ animation: false });
                this.tippyInstance.hide();
                this.tippyInstance.show();
                setTimeout(() => this.tippyInstance.setProps({ animation: 'fade' }), 300);
            }

        }, 200);
        window.addEventListener('resize', this.resizeListener);
    }

    getCardClass(metricCompleted: boolean, gameType: GameType): string {
        if (metricCompleted) {
            return gameType === GameType.Connect3 ? 'cardCompleted' : 'cardCompletedBlackout';
        }
        return gameType === GameType.Connect3 ? 'cardConfiguredConnect3' : 'cardConfiguredBlackout';
    }

    getMetricValue(id: number) {
        return this.selectedGame?.Connect3Positions?.find(position => position?.metric?.metricID === id) ?? [];
    }

    getMetricAbbrevOrGoal(connect3Metric): string {
        // File: games-player-breakdown.ts Line 661: Free space has a metricID of 0, so we need to handle that case.
        // In order to get the correct metric value, we set it to -1 to match the free space metricID in Connect3Positions.
        const metricId = connect3Metric.MetricID === 0 ? -1 : connect3Metric.MetricID;
        const gameMetricValue = this.getMetricValue(metricId);
        return gameMetricValue?.metric
            ? gameMetricValue.metric.abbrev
            : this.fallbackAbbrevText;
    }

    getTargetDisplay(connect3Metric): string {
        const gameMetricValue = this.getMetricValue(connect3Metric.MetricID);
        if (gameMetricValue.targetObj?.Target == null) return '';
        const metricDirection = Number(gameMetricValue?.metric?.direction ?? 0) === DefaultDirection.Down
            ? '\<'
            : '>';
        const formattedMetric = this.getFormattedMetric(
            gameMetricValue.targetObj.Target,
            gameMetricValue.metric.conversionType);
        return `${metricDirection} ${formattedMetric}`;
    }
}
